import firebase from "firebase/compat/app";
import "firebase/compat/messaging";
import firebaseConfig, { isFirebaseConfigured, getFirebaseVapidKey } from "../config/firebaseConfig";
import { ensureNotificationPermission, showAdminAlert } from "./showBrowserNotification";

let messagingInstance = null;
let serviceWorkerRegistrationPromise = null;
let foregroundListenerAttached = false;

const attachForegroundMessageListener = () => {
  if (!messagingInstance || foregroundListenerAttached) {
    return;
  }

  messagingInstance.onMessage((payload) => {
    if (document.visibilityState === "visible") {
      return;
    }
    showAdminAlert({
      title: payload?.notification?.title,
      description: payload?.notification?.body,
      redirectTo: payload?.data?.redirectTo,
      _id: payload?.data?.notificationId,
      notificationId: payload?.data?.notificationId,
    });
  });
  foregroundListenerAttached = true;
};

const getFirebaseApp = () => {
  if (!firebase.apps.length) {
    firebase.initializeApp(firebaseConfig);
  }
  return firebase.app();
};

const getServiceWorkerRegistration = async () => {
  if (!("serviceWorker" in navigator)) {
    return null;
  }

  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = (async () => {
      const existing = await navigator.serviceWorker.getRegistration("/");
      if (existing?.active) {
        return existing;
      }

      const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
        scope: "/",
      });
      await navigator.serviceWorker.ready;
      return registration;
    })();
  }

  return serviceWorkerRegistrationPromise;
};

export const requestAdminFcmToken = async () => {
  if (!isFirebaseConfigured()) {
    console.warn(
      "[FCM] Web push is not configured. Add REACT_APP_FIREBASE_VAPID_KEY in client/.env and restart npm start."
    );
    return null;
  }

  const permission = await ensureNotificationPermission();
  if (permission !== "granted") {
    console.warn(`[FCM] Notification permission is "${permission}".`);
    return null;
  }

  try {
    getFirebaseApp();
    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      console.warn("[FCM] Service worker registration failed.");
      return null;
    }

    messagingInstance = firebase.messaging();

    const token = await messagingInstance.getToken({
      vapidKey: getFirebaseVapidKey(),
      serviceWorkerRegistration: registration,
    });

    attachForegroundMessageListener();

    if (token) {
      console.log("[FCM] Admin web push token generated.");
    } else {
      console.warn("[FCM] getToken returned empty value.");
    }

    return token || null;
  } catch (error) {
    console.error(
      "[FCM] Failed to get admin push token:",
      error?.code || error?.message || error
    );
    return null;
  }
};

export const ensureAdminMessaging = async () => {
  await requestAdminFcmToken();
  return messagingInstance;
};

export const listenForForegroundMessages = (callback) => {
  if (!messagingInstance) {
    return () => {};
  }
  return messagingInstance.onMessage(callback);
};

export const setupAdminForegroundPush = async () => {
  await ensureAdminMessaging();
  attachForegroundMessageListener();
};
